import { ArrowRightIcon, MessageSquareIcon, PlusIcon, SearchIcon, ThumbsUpIcon } from 'lucide-react'
import { observer } from 'mobx-react-lite'
import { Trans, useTranslation } from 'react-i18next'

import { formatDateDistance } from '@campai/lib-common/i18n'
import { markdownToPlainText } from '@campai/lib-common/utils/misc'
import { Actor, ActorName, Paginator } from '@campai/lib-web/components'
import {
    Button,
    Card,
    CardContent,
    CardHeader,
    Item,
    ItemActions,
    ItemContent,
    ItemDescription,
    ItemGroup,
    ItemMedia,
    ItemTitle,
    Link,
    Skeleton,
} from '@campai/lib-web/components/new/ui'
import { type RouterOutput } from '@campai/lib-web/utils/trpc'

import { StatusBadge } from '@/product/feedback/shared/StatusBadge'
import { TagBadges } from '@/product/feedback/shared/TagBadges'
import { pageSize, type Search } from '@/product/feedback/home/routeUtils'

type TicketListItem = RouterOutput['product']['feedback']['listTickets']['tickets'][number]

type TicketListProps = {
    tickets: TicketListItem[] | null
    ticketCount: number | null
    submittedSearchTerm: string
    canCreateTicket: boolean
    setTicketDialogOpen: (open: boolean) => void
    getListSearch: (overrides?: Partial<Search>) => Search
}

export const TicketList = observer(
    ({
        tickets,
        ticketCount,
        submittedSearchTerm,
        canCreateTicket,
        setTicketDialogOpen,
        getListSearch,
    }: TicketListProps) => {
        const { i18n, t } = useTranslation()

        return (
            <div className="space-y-3">
                {!tickets &&
                    Array.from({ length: 3 }).map((_, index) => (
                        <Card key={`feedback-ticket-skeleton-${index.toString()}`}>
                            <CardHeader>
                                <Skeleton className="h-5 w-1/2" />
                                <Skeleton className="h-4 w-3/4" />
                            </CardHeader>
                        </Card>
                    ))}
                {tickets?.length === 0 && ticketCount === 0 && (
                    <Card>
                        <CardContent className="flex flex-col items-center gap-3 py-10 text-center">
                            {submittedSearchTerm ? (
                                <SearchIcon className="text-muted-foreground size-8" />
                            ) : (
                                <MessageSquareIcon className="text-muted-foreground size-8" />
                            )}
                            <div className="font-medium">
                                {submittedSearchTerm
                                    ? t('product.feedback.home.searchEmpty.title')
                                    : t('product.feedback.home.empty.title')}
                            </div>
                            <div className="text-muted-foreground max-w-md text-sm">
                                {submittedSearchTerm
                                    ? t('product.feedback.home.searchEmpty.description')
                                    : t('product.feedback.home.empty.description')}
                            </div>
                            {canCreateTicket && !submittedSearchTerm && (
                                <Button onClick={() => setTicketDialogOpen(true)}>
                                    <PlusIcon data-icon="inline-start" />
                                    {t('product.feedback.home.create')}
                                </Button>
                            )}
                        </CardContent>
                    </Card>
                )}
                {!!tickets?.length && (
                    <ItemGroup className="gap-3">
                        {tickets.map((ticket) => {
                            const createdAt = ticket.createdAt
                                ? formatDateDistance(ticket.createdAt, { i18n, addSuffix: true, isDateOnly: false })
                                : ''
                            const latestActivityAt = formatDateDistance(ticket.latestActivityAt, {
                                i18n,
                                addSuffix: true,
                                isDateOnly: false,
                            })
                            const preview = markdownToPlainText(ticket.message).replace(/\s+/g, ' ').trim()

                            return (
                                <Item
                                    key={ticket._id.toString()}
                                    variant="outline"
                                    render={
                                        <Link
                                            variant="unstyled"
                                            to="/product/feedback/tickets/$ticketNumber"
                                            params={{ ticketNumber: ticket.number.toString() }}
                                            search={getListSearch()}
                                            active={false}
                                        />
                                    }
                                    className="bg-background group items-start gap-4 rounded-lg"
                                >
                                    <ItemMedia className="!translate-y-0 !self-center">
                                        <Actor size="xl" actor={ticket.createdBy!} />
                                    </ItemMedia>
                                    <ItemContent className="min-w-0 gap-1">
                                        <ItemTitle className="block w-full max-w-full truncate text-base leading-5">
                                            <span className="inline-flex max-w-full items-center gap-2">
                                                <span className="min-w-0 truncate">{ticket.title}</span>
                                                <StatusBadge status={ticket.status} />
                                                <TagBadges tags={ticket.tags} />
                                            </span>
                                        </ItemTitle>
                                        <ItemDescription className="block w-full max-w-full truncate leading-4">
                                            {preview}
                                        </ItemDescription>
                                        <div className="mt-1.5 truncate text-xs leading-4">
                                            <Trans
                                                i18nKey="product.feedback.home.ticketMeta"
                                                values={{ number: ticket.number, createdAt, latestActivityAt }}
                                                components={{ author: <ActorName actor={ticket.createdBy!} /> }}
                                            />
                                        </div>
                                    </ItemContent>
                                    <ItemActions className="text-muted-foreground ml-auto hidden shrink-0 items-center justify-end gap-4 self-center text-sm sm:flex">
                                        <span className="inline-flex items-center gap-1">
                                            <ThumbsUpIcon className="size-4" />
                                            {ticket.votesCount}
                                        </span>
                                        <span className="inline-flex items-center gap-1">
                                            <MessageSquareIcon className="size-4" />
                                            {ticket.commentsCount}
                                        </span>
                                        <ArrowRightIcon className="size-5 shrink-0 transition-transform group-hover:translate-x-0.5" />
                                    </ItemActions>
                                </Item>
                            )
                        })}
                    </ItemGroup>
                )}
                {ticketCount !== null && ticketCount > pageSize && (
                    <Paginator variant="footer" to="/product/feedback" pageSize={pageSize} count={ticketCount} />
                )}
            </div>
        )
    }
)
